/**
 * Employee Migration Utility
 * Creates Employee records from existing User data
 * Part of Phase 5: Employee Type Implementation
 */

import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { listUsers, listEmployees } from 'graphql/queries';
import { createEmployee, updateUser } from 'graphql/mutations';

/**
 * Migrate existing Users to Employee records
 * Creates bidirectional linking between User and Employee
 * @param {string} clientId - Client identifier
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - Additional options
 * @returns {Object} Migration results
 */
export async function migrateUsersToEmployees(clientId, onProgress = () => {}, options = {}) {
  const { forceOverwrite = false } = options;
  
  const results = {
    created: 0,
    linked: 0,
    skipped: 0,
    overwritten: 0,
    errors: [],
    employeeMap: new Map(), // Maps userId to employeeId
    totalUsers: 0,
    userDetails: [] // Detailed info for each user
  };

  try {
    onProgress({ message: 'Fetching existing users...', phase: 'fetching' });
    
    // Fetch all users for the client
    let allUsers = [];
    let nextToken = null;
    
    do {
      const userData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { client: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = userData.data.listUsers?.items || [];
      allUsers = allUsers.concat(items);
      nextToken = userData.data.listUsers?.nextToken;
    } while (nextToken);
    
    results.totalUsers = allUsers.length;
    onProgress({ 
      message: `Found ${allUsers.length} users to process`, 
      phase: 'starting',
      totalUsers: allUsers.length 
    });

    // Process each user with detailed tracking
    for (let index = 0; index < allUsers.length; index++) {
      const user = allUsers[index];
      const userNum = index + 1;
      const userDetail = {
        number: userNum,
        userName: user.userName,
        userId: user.id,
        status: 'pending',
        message: '',
        employeeId: null
      };
      
      try {
        onProgress({ 
          message: `Processing user ${userNum}/${allUsers.length}: ${user.userName}`,
          phase: 'processing',
          current: userNum,
          total: allUsers.length,
          userName: user.userName
        });
        
        // Check if user already has an employeeId
        if (user.employeeId && !forceOverwrite) {
          console.log(`[${userNum}/${allUsers.length}] User ${user.userName} already linked to employee ${user.employeeId}`);
          userDetail.status = 'skipped';
          userDetail.message = `Already has employeeId: ${user.employeeId}`;
          userDetail.employeeId = user.employeeId;
          results.skipped++;
          results.employeeMap.set(user.id, user.employeeId);
          results.userDetails.push(userDetail);
          
          onProgress({ 
            message: `[${userNum}/${allUsers.length}] Skipped: ${user.userName} (already has employee)`,
            phase: 'skipped',
            current: userNum,
            total: allUsers.length,
            userName: user.userName,
            reason: 'Already linked'
          });
          continue;
        }
        
        // Handle overwrite scenario
        if (user.employeeId && forceOverwrite) {
          console.log(`[${userNum}/${allUsers.length}] Overwriting employee for user ${user.userName}`);
          userDetail.message = `Overwriting existing employeeId: ${user.employeeId}`;
          results.overwritten++;
        }

        // Create Employee record
        const employeeInput = {
          clientId,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || `${user.userName}@placeholder.com`,
          isActive: true,
          employmentType: 'FULL_TIME', // Default assumption
          userId: user.id, // Link to User
          metadata: JSON.stringify({
            migratedFromUser: true,
            migrationDate: new Date().toISOString(),
            originalUserName: user.userName,
            originalRole: user.role,
            overwritten: forceOverwrite && user.employeeId ? true : false
          }),
          createdAt: new Date().toISOString(),
          lastModified: new Date().toISOString()
        };

        const employeeResult = await API.graphql(
          graphqlOperation(createEmployee, { input: employeeInput })
        );
        
        const employeeId = employeeResult.data.createEmployee.id;
        results.employeeMap.set(user.id, employeeId);
        results.created++;
        
        userDetail.employeeId = employeeId;
        userDetail.status = 'created';
        userDetail.message = `Created new employee: ${employeeId}`;

        // Update User with employeeId for bidirectional link
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: user.id,
              employeeId: employeeId
            }
          })
        );
        results.linked++;
        
        userDetail.status = 'completed';
        userDetail.message = `Successfully created and linked employee: ${employeeId}`;

        onProgress({ 
          message: `[${userNum}/${allUsers.length}] ✅ Created employee for: ${user.userName}`,
          phase: 'created',
          current: userNum,
          total: allUsers.length,
          userName: user.userName,
          employeeId: employeeId
        });
        
      } catch (error) {
        console.error(`[${userNum}/${allUsers.length}] Error migrating user ${user.userName}:`, error);
        userDetail.status = 'error';
        userDetail.message = `Error: ${error.message}`;
        results.errors.push({
          number: userNum,
          userId: user.id,
          userName: user.userName,
          error: error.message
        });
        
        onProgress({ 
          message: `[${userNum}/${allUsers.length}] ❌ Failed: ${user.userName} - ${error.message}`,
          phase: 'error',
          current: userNum,
          total: allUsers.length,
          userName: user.userName,
          error: error.message
        });
      }
      
      results.userDetails.push(userDetail);
    }

    // Generate summary
    const summary = {
      totalProcessed: allUsers.length,
      successful: results.created,
      linked: results.linked,
      skipped: results.skipped,
      overwritten: results.overwritten,
      failed: results.errors.length
    };

    onProgress({ 
      message: `Migration complete: ${results.created} created, ${results.linked} linked, ${results.skipped} skipped, ${results.errors.length} errors`,
      phase: 'complete',
      summary
    });
    
    results.summary = summary;
    return results;
    
  } catch (error) {
    console.error('Fatal error in user to employee migration:', error);
    onProgress({ 
      message: `Fatal error: ${error.message}`,
      phase: 'fatal-error',
      error: error.message
    });
    throw error;
  }
}

/**
 * Get or create Employee for a User
 * Helper function for location/orgunit assignment migrations
 * @param {Object} user - User object
 * @param {string} clientId - Client identifier
 * @returns {string} Employee ID
 */
export async function getOrCreateEmployeeForUser(user, clientId) {
  // If user already has employeeId, return it
  if (user.employeeId) {
    return user.employeeId;
  }

  // Create new Employee
  const employeeInput = {
    clientId,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || `${user.userName}@placeholder.com`,
    isActive: true,
    employmentType: 'FULL_TIME',
    userId: user.id,
    metadata: JSON.stringify({
      migratedFromUser: true,
      migrationDate: new Date().toISOString()
    }),
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };

  const result = await API.graphql(
    graphqlOperation(createEmployee, { input: employeeInput })
  );

  // Update User with employeeId
  await API.graphql(
    graphqlOperation(updateUser, {
      input: {
        id: user.id,
        employeeId: result.data.createEmployee.id
      }
    })
  );

  return result.data.createEmployee.id;
}

/**
 * Validate User to Employee migration integrity
 * Checks that every User has exactly one Employee record
 * @param {string} clientId - Client identifier
 * @returns {Object} Validation results
 */
export async function validateUserEmployeeMigration(clientId) {
  const results = {
    valid: true,
    totalUsers: 0,
    totalEmployees: 0,
    usersWithoutEmployee: [],
    usersWithMultipleEmployees: [],
    employeesWithoutUser: [],
    duplicateEmployees: [],
    errors: []
  };

  try {
    console.log('=== Starting User-Employee Migration Validation ===');
    console.log(`Client ID: ${clientId}`);
    
    // Fetch all Users
    console.log('\n1. Fetching all Users...');
    let allUsers = [];
    let nextToken = null;
    
    do {
      const userData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { client: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = userData.data.listUsers?.items || [];
      allUsers = allUsers.concat(items);
      nextToken = userData.data.listUsers?.nextToken;
    } while (nextToken);
    
    results.totalUsers = allUsers.length;
    console.log(`   Found ${allUsers.length} Users`);

    // Fetch all Employees
    console.log('\n2. Fetching all Employees...');
    let allEmployees = [];
    nextToken = null;
    
    do {
      const employeeData = await API.graphql(
        graphqlOperation(listEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = employeeData.data.listEmployees?.items || [];
      allEmployees = allEmployees.concat(items);
      nextToken = employeeData.data.listEmployees?.nextToken;
    } while (nextToken);
    
    results.totalEmployees = allEmployees.length;
    console.log(`   Found ${allEmployees.length} Employees`);

    // High-level count validation
    console.log('\n3. High-level count validation:');
    console.log(`   Users: ${results.totalUsers}`);
    console.log(`   Employees: ${results.totalEmployees}`);
    
    if (results.totalUsers !== results.totalEmployees) {
      console.warn(`   ⚠️ MISMATCH: User count (${results.totalUsers}) != Employee count (${results.totalEmployees})`);
      results.valid = false;
    } else {
      console.log(`   ✅ Count match: ${results.totalUsers} Users = ${results.totalEmployees} Employees`);
    }

    // Build Employee maps for validation
    console.log('\n4. Building validation maps...');
    const employeesByUserId = new Map(); // userId -> Employee[]
    const employeesById = new Map(); // employeeId -> Employee
    
    allEmployees.forEach(employee => {
      employeesById.set(employee.id, employee);
      
      if (employee.userId) {
        if (!employeesByUserId.has(employee.userId)) {
          employeesByUserId.set(employee.userId, []);
        }
        employeesByUserId.get(employee.userId).push(employee);
      } else {
        // Employee without userId
        results.employeesWithoutUser.push({
          employeeId: employee.id,
          name: `${employee.firstName} ${employee.lastName}`.trim(),
          email: employee.email
        });
      }
    });

    // Validate each User
    console.log('\n5. Validating User-Employee relationships...');
    let validCount = 0;
    
    for (const user of allUsers) {
      const userName = user.userName;
      const userId = user.id;
      
      // Check if User has employeeId field
      if (!user.employeeId) {
        results.usersWithoutEmployee.push({
          userId,
          userName,
          name: `${user.firstName} ${user.lastName}`.trim()
        });
        console.log(`   ❌ User "${userName}" has no employeeId field`);
        results.valid = false;
        continue;
      }
      
      // Check if the employeeId exists
      if (!employeesById.has(user.employeeId)) {
        results.errors.push({
          userId,
          userName,
          error: `User has employeeId ${user.employeeId} but Employee record not found`
        });
        console.log(`   ❌ User "${userName}" points to non-existent Employee ${user.employeeId}`);
        results.valid = false;
        continue;
      }
      
      // Check for multiple Employees linked to this User
      const linkedEmployees = employeesByUserId.get(userId) || [];
      if (linkedEmployees.length === 0) {
        results.errors.push({
          userId,
          userName,
          error: `User has employeeId but no Employee records point back to this User`
        });
        console.log(`   ❌ User "${userName}" has employeeId but no Employee.userId points back`);
        results.valid = false;
      } else if (linkedEmployees.length > 1) {
        results.usersWithMultipleEmployees.push({
          userId,
          userName,
          employeeCount: linkedEmployees.length,
          employeeIds: linkedEmployees.map(e => e.id)
        });
        console.log(`   ❌ User "${userName}" has ${linkedEmployees.length} Employee records`);
        results.valid = false;
        
        // Mark duplicates
        linkedEmployees.forEach(emp => {
          results.duplicateEmployees.push({
            employeeId: emp.id,
            userId: emp.userId,
            userName,
            name: `${emp.firstName} ${emp.lastName}`.trim(),
            isActive: emp.isActive
          });
        });
      } else if (linkedEmployees[0].id !== user.employeeId) {
        results.errors.push({
          userId,
          userName,
          error: `User.employeeId (${user.employeeId}) doesn't match Employee.id (${linkedEmployees[0].id})`
        });
        console.log(`   ❌ User "${userName}" employeeId mismatch`);
        results.valid = false;
      } else {
        validCount++;
      }
    }
    
    console.log(`   ✅ ${validCount} Users have valid 1:1 Employee relationships`);

    // Summary
    console.log('\n=== Validation Summary ===');
    console.log(`Total Users: ${results.totalUsers}`);
    console.log(`Total Employees: ${results.totalEmployees}`);
    console.log(`Valid 1:1 relationships: ${validCount}`);
    
    if (results.usersWithoutEmployee.length > 0) {
      console.log(`\n❌ Users without Employee (${results.usersWithoutEmployee.length}):`);
      results.usersWithoutEmployee.forEach(u => {
        console.log(`   - ${u.userName} (${u.name})`);
      });
    }
    
    if (results.usersWithMultipleEmployees.length > 0) {
      console.log(`\n❌ Users with multiple Employees (${results.usersWithMultipleEmployees.length}):`);
      results.usersWithMultipleEmployees.forEach(u => {
        console.log(`   - ${u.userName}: ${u.employeeCount} employees [${u.employeeIds.join(', ')}]`);
      });
    }
    
    if (results.employeesWithoutUser.length > 0) {
      console.log(`\n❌ Orphaned Employees without User (${results.employeesWithoutUser.length}):`);
      results.employeesWithoutUser.forEach(e => {
        console.log(`   - ${e.employeeId}: ${e.name} (${e.email})`);
      });
    }
    
    if (results.errors.length > 0) {
      console.log(`\n❌ Other errors (${results.errors.length}):`);
      results.errors.forEach(e => {
        console.log(`   - ${e.userName}: ${e.error}`);
      });
    }
    
    if (results.valid) {
      console.log('\n✅ VALIDATION PASSED: All Users have exactly one Employee record');
    } else {
      console.log('\n❌ VALIDATION FAILED: Issues found in User-Employee relationships');
      console.log('\nRecommended action: Clear all Employees and re-run migration');
    }
    
    console.log('\n=== End of Validation ===');
    return results;
    
  } catch (error) {
    console.error('Fatal error during validation:', error);
    results.errors.push({ error: error.message });
    results.valid = false;
    return results;
  }
}